import { marketingEngedelyezett } from './consent'

/**
 * META PIXEL (Facebook / Instagram hirdetesmeres).
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT NEM AZ `index.html`-BEN VAN
 *
 * A Meta altal adott kodreszlet a `<head>`-be valo, es AZONNAL betolti a
 * `fbevents.js`-t. Ket okbol nem oda tettuk:
 *
 *   1. HOZZAJARULAS. A pixelnek a latogato dontese szerint kell
 *      elindulnia (`consent.ts`). Egy `<head>`-be irt szkriptet mar nem
 *      lehet visszatartani - mire a React elindul, a kapcsolat felepult.
 *   2. KAPCSOLHATOSAG. Az azonosito kornyezeti valtozobol jon, mint az
 *      oldal minden mas publikus beallitasa. Ha ures, ez a modul TELJESEN
 *      inert: nincs `fbq`, nincs szkript, nincs halozati keres.
 *
 * A latogato szamara a kulonbseg nem erzekelheto: a szkript ugyanaz, es
 * ugyanugy aszinkron tolt.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT NINCS `<noscript>` KEPPONT
 *
 * A Meta kodreszlete tartalmaz egy `<noscript><img ...>` tartalekot. Itt
 * ERTELMETLEN lenne: az oldal egy React alkalmazas, JavaScript nelkul a
 * `<div id="root">` uresen marad. Aki nem futtat JavaScriptet, az nem
 * latogato, hanem robot - rola meresi adatot gyujteni felrevezeto szamot
 * adna, jogalapot viszont nem.
 * ────────────────────────────────────────────────────────────────────
 */

const PIXEL_ID = (
  (import.meta.env as Record<string, string | undefined>).VITE_META_PIXEL_ID ?? ''
).trim()

/** Ures azonosito = a pixel ki van kapcsolva, a modul nem csinal semmit. */
export const pixelBeallitva = PIXEL_ID.length > 0

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  push: unknown
  loaded: boolean
  version: string
}

interface PixelAblak extends Window {
  fbq?: Fbq
  _fbq?: Fbq
}

const SZKRIPT = 'https://connect.facebook.net/en_US/fbevents.js'

let elindult = false
let letiltva = false
/** Az utolso jelentett ut. Ez akadalyozza meg a dupla `PageView`-t. */
let utolsoUt: string | null = null

/**
 * A META ALTAL ADOTT BETOLTO, VALTOZATLAN MUKODESSEL.
 *
 * Sorrol sorra ugyanaz, mint a hivatalos kodreszlet - csak tipusokkal es
 * olvashato nevekkel. A `queue` azert kell, hogy a szkript megerkezese
 * ELOTT kiadott hivasok se vesszenek el.
 */
function betolto(): void {
  const w = window as PixelAblak
  if (w.fbq) return

  const n = function (...args: unknown[]): void {
    if (n.callMethod) n.callMethod.apply(n, args)
    else n.queue.push(args)
  } as Fbq

  w.fbq = n
  if (!w._fbq) w._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  const t = document.createElement('script')
  t.async = true
  t.src = SZKRIPT

  /*
    A hivatalos kodreszlet az ELSO `<script>` ELE szurja be. Itt van ilyen
    (a modul-belepesi pont), de egy esetleges atrendezes utan is mukodnie
    kell - ezert van tartalek ag a `<head>`-re.
  */
  const elso = document.getElementsByTagName('script')[0]
  if (elso && elso.parentNode) elso.parentNode.insertBefore(t, elso)
  else document.head.appendChild(t)
}

function fbq(...args: unknown[]): void {
  const w = window as PixelAblak
  if (w.fbq) w.fbq(...args)
}

/**
 * A PIXEL INDITASA.
 *
 * Tobbszor is hivhato: masodszorra nem csinal semmit. Ez SZANDEKOS - a
 * hozzajarulas valtozasat figyelo hatas minden allapotvaltasnal meghivja.
 */
export function pixelIndit(): void {
  if (!pixelBeallitva) return
  if (!marketingEngedelyezett()) return

  /*
    ELOSZOR ELUTASITOTTA, AZTAN MEGIS ELFOGADTA.

    Ilyenkor a szkript mar betoltott (`elindult`), csak `revoke`-ot
    kapott. Ha itt egyszeruen visszaternenk, az ujboli elfogadas
    SEMMIT nem csinalna: a panel bezarodna, a meres viszont allna
    tovabb - es ez sehol nem latszana, csak a hianyzo szamokban.
  */
  if (elindult) {
    if (letiltva) {
      letiltva = false
      fbq('consent', 'grant')
      pixelOldalmegtekintes()
    }
    return
  }

  betolto()

  /*
    ════════════════════════════════════════════════════════════════
    AUTOMATIKUS ADATGYUJTES KIKAPCSOLVA - EZ A SOR NEM DISZITES
    ════════════════════════════════════════════════════════════════

    Az `autoConfig: false` ket dolgot kapcsol ki:

      - AUTOMATIKUS BOVITETT PAROSITAS (Automatic Advanced Matching):
        enelkul a pixel MAGATOL kiolvassa a lapon levo urlapmezoket -
        e-mail-cimet, telefonszamot, nevet -, es elkuldi a Metanak.
        Az arbecslo osszegzo lepesen PONTOSAN ilyen mezok vannak.
      - automatikus esemenyfelismeres (gombkattintasok).

    Az `analytics.ts` egesz felepitese arra megy ki, hogy szemelyes adat
    ne hagyja el az oldalt. Az automatikus parositas ezt egyetlen
    tavolrol bekapcsolhato kapcsoloval kerulne meg.

    FIGYELEM: ez a beallitas a KLIENS oldalon tiltja le. A Meta
    Esemenykezelojeben (Events Manager -> Beallitasok) ugyanez kulon
    kapcsolokent is szerepel, es AJANLOTT ott is kikapcsolni - az a
    szerveroldali alapertelmezes, amit innen nem latunk.
  */
  fbq('set', 'autoConfig', false, PIXEL_ID)

  /*
    Az `init` MASODIK argumentum nelkul: igy nem megy at semmilyen
    felhasznaloi adat (kezi bovitett parositas).
  */
  fbq('init', PIXEL_ID)

  elindult = true
  letiltva = false
  pixelOldalmegtekintes()
}

/**
 * A HOZZAJARULAS VISSZAVONASA.
 *
 * A mar betoltott szkriptet nem lehet visszaszedni a lapbol, de harom
 * dolgot meg lehet tenni, es mindharmat megtesszuk:
 *
 *   1. `consent revoke` - a Meta sajat kapcsoloja, a tovabbi esemenyek
 *      nem mennek ki;
 *   2. sajat zar (`letiltva`) - ide be sem lepunk tobbe;
 *   3. a pixel altal irt `_fbp` es `_fbc` suti TORLESE - kulonben a
 *      visszavonas utan is ott maradna a bongeszoben az az azonosito,
 *      amit epp nem akar a latogato.
 */
export function pixelLeallit(): void {
  letiltva = true
  utolsoUt = null
  if (elindult) fbq('consent', 'revoke')
  sutitTorol('_fbp')
  sutitTorol('_fbc')
}

/**
 * Suti torlese minden szoba joheto tartomanybeallitassal.
 *
 * A `_fbp` sutit a `fbevents.js` irja, es NEM tudjuk biztosan, milyen
 * `domain` ertekkel - ezert mindharom valtozatot lejaratjuk: tartomany
 * nelkul (hoszthoz kotott), `.<hoszt>`, es `.<regisztralt domain>`.
 */
function sutitTorol(nev: string): void {
  try {
    const hoszt = location.hostname
    const reszek = hoszt.split('.')
    const fo = reszek.length > 2 ? reszek.slice(-2).join('.') : hoszt

    const tartomanyok = ['', '; domain=' + hoszt, '; domain=.' + hoszt, '; domain=.' + fo]
    for (const t of tartomanyok) {
      document.cookie = nev + '=; Max-Age=0; path=/' + t
    }
  } catch {
    // Sutik tiltva: nincs is mit torolni.
  }
}

function fut(): boolean {
  return pixelBeallitva && elindult && !letiltva
}

/**
 * OLDALMEGTEKINTES.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT AZ UT ALAPJAN SZURUNK
 *
 * Ket helyrol hivodik: a `pixelIndit` vegerol (az elso megtekintes), es
 * az utvonalvaltast figyelo hatasbol (`main.tsx`). Indulaskor a ketto
 * KOZVETLENUL egymas utan futna le - ugyanarra az oldalra ket
 * `PageView`-t kuldve, ami a Meta feluleten megduplazna a
 * megtekintesszamot.
 *
 * Az utvonal osszehasonlitasa ezt kizarja, es kozben tobbet is ad, mint
 * a puszta "elso hivas" zar: a `/ajanlat/A` -> `/ajanlat/B` valtas ket
 * KULON megtekintesnek szamit, ahogy kell.
 * ────────────────────────────────────────────────────────────────────
 */
export function pixelOldalmegtekintes(): void {
  if (!fut()) return

  const most = location.pathname
  if (most === utolsoUt) return
  utolsoUt = most

  fbq('track', 'PageView')
}

/**
 * SZABVANYOS ESEMENY.
 *
 * A parametereket az `analytics.ts` mar megszurte; ide csak szam- es
 * rovid szoveges ertek jut el.
 */
export function pixelEsemeny(
  nev: string,
  parameterek: Record<string, string | number | boolean> = {}
): void {
  if (!fut()) return
  fbq('track', nev, parameterek)
}

/** CSAK TESZTHEZ: a modul belso allapotanak visszaallitasa. */
export function __resetPixel(): void {
  elindult = false
  letiltva = false
  utolsoUt = null
  const w = window as PixelAblak
  delete w.fbq
  delete w._fbq
}

/** CSAK TESZTHEZ: fut-e eppen a pixel. */
export function __pixelFut(): boolean {
  return fut()
}
